import { ATTRIBUTION_WINDOW_DAYS } from '../lib/affiliateProgram';

// Affiliate referral capture.
//
// An affiliate link is `getanyapi.com/?via=<code>`. This reads that code off
// the URL and parks it in a first-party cookie, so it survives the pages a
// visitor reads before they decide to buy. The gateway does the rest: at
// checkout it claims the code against the account, once, and from then on the
// account owns the answer and this cookie is irrelevant.
//
// This replaced a hosted third-party tag, which is why there is no script load
// here and no idle deferral. Reading a query parameter and setting a cookie is
// local work measured in microseconds, so it runs on load instead of waiting
// for the browser to go idle - and that removes the capture gap the old tag
// had, where a visitor who bounced inside the first few hundred milliseconds
// was never attributed at all. It also works on localhost, which the hosted
// tag deliberately did not.

const COOKIE = 'anyapi_via';

/** The affiliate link's query parameter, the convention affiliate links use. */
const PARAM = 'via';

/** Mirrors the gateway's own SanitizeCode. A code that cannot be ours is
 *  dropped here rather than stored and sent, so a crafted link cannot park
 *  arbitrary text in a cookie we later hand to the API. The gateway sanitizes
 *  again regardless - this is the cheap half of the same rule, not the only
 *  one. */
const CODE = /^[A-Za-z0-9]{4,32}$/;

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

/**
 * Store the `?via=` code from the current URL, if there is a valid one, and
 * report it so the caller can count the arrival.
 *
 * Last link wins while the visitor is still anonymous: someone who reads one
 * affiliate's post, wanders off, and comes back through a second affiliate's
 * link is here because of the second one. That only holds until they pay - the
 * gateway's claim never overwrites, so once an account is attributed the first
 * affiliate keeps it for the whole six months.
 *
 * It returns the code rather than reporting the arrival itself, so this stays a
 * pure reader of the URL and writer of the cookie with no transport behind it.
 * The composition root owns the network call; see Providers.
 */
export function captureAffiliateReferral(): string | undefined {
  if (!isBrowser()) return undefined;
  let code: string | null = null;
  try {
    code = new URL(window.location.href).searchParams.get(PARAM);
  } catch {
    // A URL the browser accepted but URL() will not parse. Nothing to capture.
    return undefined;
  }
  if (!code || !CODE.test(code)) return undefined;

  const expires = new Date(Date.now() + ATTRIBUTION_WINDOW_DAYS * 86_400_000).toUTCString();
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  // Lax rather than Strict: a visitor can arrive on an affiliate link from
  // somebody else's post, which is a cross-site navigation, and Strict would
  // withhold the cookie on exactly the request that matters.
  document.cookie = `${COOKIE}=${code}; Path=/; Expires=${expires}; SameSite=Lax${secure}`;
  return code;
}

/** The affiliate this visit belongs to, or undefined when there is none. */
export function affiliateReferral(): string | undefined {
  if (!isBrowser()) return undefined;
  for (const part of document.cookie.split(';')) {
    const [name, ...rest] = part.trim().split('=');
    if (name !== COOKIE) continue;
    const value = rest.join('=');
    return CODE.test(value) ? value : undefined;
  }
  return undefined;
}
